import { normalizeFlagToBool } from '@/utils/flag'

export function getFirstCustomRecord(custom) {
    if (!Array.isArray(custom) || custom.length === 0) return null
    const first = custom[0]
    return first && typeof first === 'object' ? first : null
}

/**
 * Construye un mapa { itemId: customArray } recorriendo todos los templates
 * del objeto dado. Soporta templates con items planos (array u objeto) y con
 * secciones que contienen items. Sólo indexa items cuyo `custom` sea un array
 * no vacío (es decir, que hayan sido editados localmente).
 *
 * @param {Object} templatesObj  Objeto { templateId: templateObject, ... }
 * @returns {Object}             Mapa { templateItemId: customArray }
 */
export function buildItemCustomMapFromTemplates(templatesObj) {
    const map = {}
    if (!templatesObj || typeof templatesObj !== 'object') return map

    Object.values(templatesObj).forEach((tpl) => {
        if (!tpl || typeof tpl !== 'object') return
        _collectCustomFromTemplate(tpl, map)
    })

    return map
}

/**
 * Combina los campos faltantes del custom del template en el custom del item.
 * Solo rellena campos que sean null/undefined/vacíos en el registro del item,
 * sin sobreescribir valores que el item ya tenga definidos.
 * Excepción: el campo `apply` siempre se toma del template (configuración de visibilidad).
 *
 * @param {Object} item               Item del registro/tarea con item.custom
 * @param {Array}  templateCustomArray Array custom del template para ese item
 */
export function mergeTemplateCustomIntoItem(item, templateCustomArray) {
    const itemRecord = getFirstCustomRecord(item.custom)
    const tplRecord = getFirstCustomRecord(templateCustomArray)
    if (!itemRecord || !tplRecord) return

    if (
        (itemRecord.title == null || String(itemRecord.title).trim() === '') &&
        tplRecord.title != null
    ) {
        itemRecord.title = tplRecord.title
    }

    if (
        (itemRecord.description == null || String(itemRecord.description).trim() === '') &&
        tplRecord.description != null
    ) {
        itemRecord.description = tplRecord.description
    }

    if (
        (itemRecord.equipment_id == null || String(itemRecord.equipment_id).trim() === '') &&
        tplRecord.equipment_id != null
    ) {
        itemRecord.equipment_id = tplRecord.equipment_id
    }

    if (tplRecord.apply != null) {
        itemRecord.apply = tplRecord.apply
    }
}

/**
 * Convierte la fecha de creación del registro a timestamp (ms) para comparar.
 * Acepta: number (unix segundos), Date, moment-like (.valueOf()), o string ISO/datetime.
 *
 * @param {number|string|Date|Object} registerCreatedDate
 * @returns {number} Timestamp en milisegundos, o 0 si no se puede parsear
 */
function parseRegisterCreatedDate(registerCreatedDate) {
    if (registerCreatedDate == null) return 0
    if (typeof registerCreatedDate === 'number') {
        return registerCreatedDate < 1e12 ? registerCreatedDate * 1000 : registerCreatedDate
    }
    if (registerCreatedDate instanceof Date) return registerCreatedDate.getTime()
    if (typeof registerCreatedDate === 'object' && typeof registerCreatedDate.valueOf === 'function') {
        const v = registerCreatedDate.valueOf()
        return typeof v === 'number' ? v : 0
    }
    if (typeof registerCreatedDate === 'string') {
        const parsed = Date.parse(registerCreatedDate)
        return Number.isNaN(parsed) ? 0 : parsed
    }
    return 0
}

/**
 * Convierte custom.created_at (string datetime ej. "2026-03-02 20:01:57") a timestamp ms en UTC.
 * Sin sufijo de zona, Date.parse interpreta como hora local; aquí forzamos UTC para que
 * sea comparable con parseRegisterCreatedDate (moment/Unix en UTC).
 *
 * @param {string} createdAt
 * @returns {number}
 */
function parseCustomCreatedAt(createdAt) {
    if (createdAt == null || typeof createdAt !== 'string') return 0
    const normalized = createdAt.trim().replace(' ', 'T')
    const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(normalized)
    const toParse = hasZone ? normalized : normalized + 'Z'
    const parsed = Date.parse(toParse)
    return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Obtiene el apply efectivo para un item del template cuando se muestra en un registro
 * con fecha de creación dada. Se usa el custom vigente en la fecha del registro:
 * aquel cuya created_at sea menor o igual a la fecha de creación del registro
 * (si hay varios, el más reciente de esos).
 *
 * @param {Object} templateItem  Item del template (puede tener custom[])
 * @param {number|string|Date|Object} registerCreatedDate  Fecha de creación del registro
 * @returns {number} 1 = mostrar, 0 = ocultar
 */
export function getCustomApplyForRegister(templateItem, registerCreatedDate) {
    if (!templateItem || !Array.isArray(templateItem.custom) || templateItem.custom.length === 0) {
        const fallback = templateItem && templateItem.apply != null ? Number(templateItem.apply) : 1
        return Number.isNaN(fallback) ? 1 : fallback
    }

    const registerTs = parseRegisterCreatedDate(registerCreatedDate)
    if (registerTs === 0) {
        const fallback = templateItem.apply != null ? Number(templateItem.apply) : 1
        return Number.isNaN(fallback) ? 1 : fallback
    }

    const applicable = templateItem.custom
        .map((c) => ({ custom: c, ts: parseCustomCreatedAt(c && c.created_at) }))
        .filter(({ ts }) => ts > 0 && ts <= registerTs)
        .sort((a, b) => b.ts - a.ts)

    if (applicable.length === 0) {
        const fallback = templateItem.apply != null ? Number(templateItem.apply) : 1
        return Number.isNaN(fallback) ? 1 : fallback
    }

    const apply = applicable[0].custom.apply
    if (apply == null) return 1
    const n = Number(apply)
    return Number.isNaN(n) ? 1 : n
}

/**
 * Devuelve el valor efectivo del campo `apply` del registro custom de un item.
 * Si el campo no existe o no es válido, devuelve 1 (mostrar por defecto).
 *
 * @param {Object} item  Item con posible array custom
 * @returns {number}     1 = mostrar, 0 = ocultar
 */
export function getCustomApply(item) {
    const record = getFirstCustomRecord(item && item.custom)
    if (!record || record.apply == null) return 1
    const n = Number(record.apply)
    return Number.isNaN(n) ? 1 : n
}

/**
 * Determina si un item debe ser completamente oculto (no renderizado).
 * Un item se oculta SOLO cuando AMBOS item.apply Y custom.apply son 0.
 * Esto permite que los items N/A sigan mostrándose según la configuración showNoApply.
 *
 * @param {Object} item  Item con campos apply y custom
 * @returns {boolean}    true si debe ocultarse, false si debe mostrarse
 */
export function shouldHideItem(item) {
    if (!item) return true
    // No usar Number(null)===0: null/undefined deben seguir contando como visibles (itemApply=1), como con item.apply == 0
    let itemApply = 1
    if (item.apply !== null && item.apply !== undefined) {
        const n = Number(item.apply)
        if (!Number.isNaN(n) && n === 0) {
            itemApply = 0
        }
    }
    const customApply = getCustomApply(item)
    return itemApply === 0 && customApply === 0
}

const ITEM_TYPE_ICONS = {
    1: 'i-andy-done',
    2: 'i-andy-Predefined-options',
    3: 'i-andy-Predefined-options',
    4: 'i-andy-input-text',
    5: 'i-andy-camera',
    6: 'i-andy-format-number',
    7: 'i-andy-input-date',
    8: 'i-andy-done',
    9: 'i-andy-done',
    10: 'i-andy-input-text',
    11: 'i-andy-selector',
    12: 'i-andy-input-time',
    13: 'i-andy-format-datetime',
    14: 'i-andy-format-sign',
    15: 'i-andy-format-temperature',
    16: 'i-andy-format-mail',
    17: 'i-andy-blank-document',
    18: 'i-andy-chatbox',
    19: 'i-andy-format-link',
    20: 'i-andy-menu-library'
}

/**
 * Devuelve el icono correspondiente al type_id de un item de plantilla.
 * @param {number} type
 * @returns {string}
 */
export function getItemTypeIcon(type) {
    return ITEM_TYPE_ICONS[Number(type)] || 'i-andy-done'
}

/**
 * Determina si un item de plantilla puede abrirse para edición/creación.
 * Lógica de permisos compartida entre templates.vue y TemplateItems.vue.
 *
 * @param {Object}  item      Item de la plantilla
 * @param {boolean} canEdit   Permiso edit_items / conf_edit del config del template
 * @param {boolean} canCreate Permiso create_items / conf_create del config del template
 * @returns {boolean}
 */
export function canOpenEditItem(item, canEdit, canCreate) {
    const hasCustom = !!(
        getFirstCustomRecord(item && item.custom) ||
        (item && typeof item.custom === 'string' && item.custom.trim() !== '')
    )
    const isMandatory = normalizeFlagToBool(item && item.mandatory)
    if (!hasCustom) return canCreate || !isMandatory
    if (canEdit) return true
    return !isMandatory
}

/**
 * Obtiene un item de plantilla por id buscando en tpl.items y en tpl.sections[].items.
 * Coincide con _collectCustomFromTemplate para que items en secciones también se resuelvan.
 *
 * @param {Object} tpl     Template con items y/o sections
 * @param {string} itemId  Id del item
 * @returns {Object|null}  El item del template o null
 */
export function getTemplateItemById(tpl, itemId) {
    if (!tpl || itemId == null) return null
    const idStr = String(itemId)

    if (tpl.items && typeof tpl.items === 'object') {
        const items = Array.isArray(tpl.items) ? tpl.items : Object.values(tpl.items)
        const found = items.find((it) => it && String(it.id) === idStr)
        if (found) return found
    }

    if (tpl.sections && typeof tpl.sections === 'object') {
        const sections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
        for (const section of sections) {
            if (!section || !section.items) continue
            const sItems = Array.isArray(section.items) ? section.items : Object.values(section.items)
            const found = sItems.find((it) => it && String(it.id) === idStr)
            if (found) return found
        }
    }

    return null
}

function _collectCustomFromTemplate(tpl, map) {
    if (tpl.items && typeof tpl.items === 'object') {
        const items = Array.isArray(tpl.items) ? tpl.items : Object.values(tpl.items)
        items.forEach((item) => {
            if (item && item.id !== undefined && item.id !== null && Array.isArray(item.custom) && item.custom.length > 0) {
                map[item.id] = item.custom
            }
        })
    }

    if (tpl.sections && typeof tpl.sections === 'object') {
        const sections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
        sections.forEach((section) => {
            if (!section || !section.items) return
            const sItems = Array.isArray(section.items) ? section.items : Object.values(section.items)
            sItems.forEach((item) => {
                if (item && item.id !== undefined && item.id !== null && Array.isArray(item.custom) && item.custom.length > 0) {
                    map[item.id] = item.custom
                }
            })
        })
    }
}
